import { FetchInitSpotDataResponse, Spot } from './spotsTypes';

const CONNECT_TYPES_MAP = {
  facebook_disabled: 'facebook',
  google_disabled: 'google',
  twitter_disabled: 'twitter',
  email_disabled: 'email',
  disqus_disabled: 'disqus',
  microsoft_disabled: 'microsoft'
};

export function parseInitSpotData(data: FetchInitSpotDataResponse): Spot {
  const {
    avatars,
    brand_color,
    category_id,
    comment_replies_max_depth,
    emoji_keyboard_enabled,
    emojis_enabled,
    giphy_level = 'g',
    id,
    inpage_skin,
    languages,
    ltr,
    main_language,
    monetized,
    multiple_moderators_mode,
    name,
    platform,
    requires_mfa,
    rich_say_control_enabled,
    sort_by,
    sso_enabled,
    sub_category_id,
    uppercase_msgs_enabled,
    user_moderation_enabled,
    website_url
  } = data;

  return {
    avatarsStyle: avatars,
    brandColor: brand_color,
    categoryId: category_id,
    connectTypes: getConnectTypes(data),
    emojiKeyboardEnabled: emoji_keyboard_enabled,
    emojisEnabled: emojis_enabled,
    enableMonetization: monetized,
    enableRichSayControl: rich_say_control_enabled,
    gifAnimationLevel: giphy_level.toLowerCase(),
    id,
    inpageSkin: inpage_skin,
    isMultipleModeratorsMode: multiple_moderators_mode,
    isRequiredTwoStepVerification: requires_mfa,
    isRestrictedUNsEnabled: user_moderation_enabled,
    languages,
    ltr,
    mainLanguage: main_language,
    maxRepliesDepth: comment_replies_max_depth,
    name,
    platform,
    sortBy: sort_by,
    ssoEnabled: sso_enabled,
    subCategoryId: sub_category_id,
    uppercaseMsgsEnabled: uppercase_msgs_enabled,
    websiteUrl: website_url,
    isFetched: true
  };
}

function getConnectTypes(data: FetchInitSpotDataResponse): string[] {
  return Object.keys(CONNECT_TYPES_MAP).reduce<Array<string>>((acc, key) => {
    if (!data[key]) {
      acc.push(CONNECT_TYPES_MAP[key]);
    }
    return acc;
  }, []);
}

const spotPropsMap: Partial<Record<string, keyof Spot>> = {
  id: 'id',
  brand_color: 'brandColor',
  inpage_skin: 'inpageSkin',
  avatars: 'avatarsStyle',
  website_url: 'websiteUrl',
  name: 'name',
  sub_category_id: 'subCategoryId',
  category_id: 'categoryId',
  platform: 'platform',
  main_language: 'mainLanguage',
  sort_by: 'sortBy',
  comment_replies_max_depth: 'maxRepliesDepth',
  rich_say_control_enabled: 'enableRichSayControl',
  giphy_level: 'gifAnimationLevel',
  emoji_keyboard_enabled: 'emojiKeyboardEnabled',
  emojis_enabled: 'emojisEnabled',
  multiple_moderators_mode: 'isMultipleModeratorsMode',
  user_moderation_enabled: 'isRestrictedUNsEnabled',
  uppercase_msgs_enabled: 'uppercaseMsgsEnabled',
  // policy settings
  roger_mode: 'isRogerMode',
  allow_guests_to_like: 'allowGuestsToLike',
  force_register: 'forceRegister',
  // tenant settings
  external_link: 'commentsContainingExternalLinks',
  image: 'commentsContainingImages',
  animation: 'commentsContainingGifs',
  default: 'defaultPolicy',
  block_report_by_registered: 'blockReportByRegistered',
  strict_external_urls_enabled: 'strictExternalUrlsEnabled'
};

export function mapToSpotProps(data: Record<string, any>): Partial<Spot> {
  return Object.keys(data).reduce((acc, key) => {
    const spotKey = spotPropsMap[key];
    if (spotKey) {
      acc[spotKey] = data[key];
    }
    return acc;
  }, {});
}

export function hasConnectTypes(data: FetchInitSpotDataResponse): boolean {
  return Object.keys(CONNECT_TYPES_MAP).some(key => data[key] !== undefined);
}

export function getUpdatedConnectTypes(data: FetchInitSpotDataResponse, connectTypes: string[]): Partial<Spot> {
  const connectKeys = Object.keys(CONNECT_TYPES_MAP);
  const connectKey = Object.keys(data).find(key => connectKeys.includes(key));

  if (!connectKey) {
    return { id: data.id, connectTypes };
  }
  // true means that we need to remove, false means that we need to add
  const shouldAdd = connectKey && data[connectKey] === false;
  const connectValue = CONNECT_TYPES_MAP[connectKey];
  const newConnectedTypes = shouldAdd
    ? [...connectTypes, connectValue]
    : connectTypes.filter(type => type !== connectValue);

  return {
    id: data.id,
    connectTypes: newConnectedTypes
  };
}

export function toKeyValueArray(obj: Record<string, unknown>): Array<{ key: string; value: any }> {
  return Object.keys(obj).map(key => ({ key, value: obj[key] }));
}
